import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Img } from "@/components/ui/img";
import {
  CalendarCheck,
  Calendar,
  Star,
  Clock,
  CheckCircle,
  Buildings,
  Envelope,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import { CLIENT_SESSION_STATUS_CONFIG } from "@/lib/status-configs";
import { ClientBlockControls } from "./ClientBlockControls";
import { formatDate, formatDateTime } from "./helpers";
import { SESSIONS_PER_PAGE } from "./types";
import type { Client, ClientSession, PaginatedSessions } from "@/types";

const SESSION_STATUS = CLIENT_SESSION_STATUS_CONFIG;

/* ─── Info Tile ─── */

function InfoTile({
  icon: Icon,
  label,
  value,
  valueColor,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="rounded-lg border bg-muted/20 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="size-3.5" />
        {label}
      </div>
      <p
        className={cn(
          "mt-0.5 truncate text-sm font-medium",
          valueColor || "text-foreground"
        )}
      >
        {value}
      </p>
    </div>
  );
}

/* ─── Session Row ─── */

function SessionRow({ session }: { session: ClientSession }) {
  const statusCfg = SESSION_STATUS[session.status];
  const StatusIcon = statusCfg.icon;

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-muted/20 px-3 py-2.5">
      <div
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-lg",
          statusCfg.bg
        )}
      >
        <StatusIcon className={cn("size-4", statusCfg.color)} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-medium text-foreground">
            {session.serviceName || "Sesija"}
          </p>
          <span
            className={cn(
              "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
              statusCfg.bg,
              statusCfg.color
            )}
          >
            {statusCfg.label}
          </span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span>{session.therapistName}</span>
          <span>{formatDateTime(session.scheduledAt)}</span>
          <span>{session.durationMinutes} min</span>
          {session.rating && (
            <span className="inline-flex items-center gap-0.5 text-amber-600">
              <Star className="size-3" />
              {session.rating}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Sessions Pagination ─── */

function SessionsPagination({
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}) {
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-muted-foreground">
        {from}–{to} od {total}
      </span>

      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="flex size-8 items-center justify-center rounded-lg border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          <CaretLeft className="size-4" />
        </button>

        <span className="px-1 text-xs text-muted-foreground">
          {page}/{totalPages}
        </span>

        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="flex size-8 items-center justify-center rounded-lg border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
        >
          <CaretRight className="size-4" />
        </button>
      </div>
    </div>
  );
}

/* ─── Client Detail Dialog ─── */

interface ClientDetailDialogProps {
  client: Client;
  onClose: () => void;
}

export function ClientDetailDialog({ client, onClose }: ClientDetailDialogProps) {
  const [sessionFilter, setSessionFilter] = useState<string>("all");
  const [sessionPage, setSessionPage] = useState(1);

  const { data: sessionData, isLoading: sessionsLoading } = useQuery({
    queryKey: [
      "admin",
      "clients",
      client.id,
      "sessions",
      sessionPage,
      sessionFilter,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(sessionPage),
        limit: String(SESSIONS_PER_PAGE),
      });
      if (sessionFilter !== "all") params.set("status", sessionFilter);
      const res = await fetch(
        `/api/clients/${client.id}/sessions?${params}`
      );
      if (!res.ok) throw new Error("Failed to fetch sessions");
      return res.json() as Promise<PaginatedSessions>;
    },
    placeholderData: (prev) => prev,
  });

  const sessions = sessionData?.data ?? [];
  const sessionTotalPages = sessionData?.totalPages ?? 1;
  const sessionTotal = sessionData?.total ?? 0;

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <div className="flex items-center gap-3">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/8">
            {client.image ? (
              <Img
                src={client.image}
                alt={client.name}
                className="size-full rounded-full object-cover"
              />
            ) : (
              <span
                className="text-lg font-bold text-primary"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {client.name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <DialogTitle className="truncate">{client.name}</DialogTitle>
              {client.blockedAt && (
                <span className="shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700 ring-1 ring-rose-200">
                  Blokiran
                </span>
              )}
              {client.repeatCancelFlag && !client.blockedAt && (
                <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 ring-1 ring-amber-200">
                  2× otkazao zaredom
                </span>
              )}
            </div>
            <DialogDescription>{client.email}</DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <DialogBody>
        <div className="flex flex-col gap-6">
          {/* Info grid */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <InfoTile
              icon={CalendarCheck}
              label="Ukupno sesija"
              value={String(client.sessionCount)}
            />
            <InfoTile
              icon={CheckCircle}
              label="Završene"
              value={String(client.completedSessions)}
              valueColor="text-emerald-700"
            />
            <InfoTile
              icon={Calendar}
              label="Predstojeće"
              value={String(client.upcomingSessions)}
              valueColor="text-blue-600"
            />
            {client.avgRating != null && (
              <InfoTile
                icon={Star}
                label="Prosječna ocjena"
                value={client.avgRating.toFixed(1)}
                valueColor="text-amber-600"
              />
            )}
            {client.companyName && (
              <InfoTile
                icon={Buildings}
                label="Firma"
                value={client.companyName}
              />
            )}
            <InfoTile
              icon={Envelope}
              label="Email verifikovan"
              value={client.emailVerified ? "Da" : "Ne"}
              valueColor={
                client.emailVerified ? "text-emerald-700" : "text-rose-600"
              }
            />
            <InfoTile
              icon={Clock}
              label="Registrovan"
              value={formatDate(client.createdAt)}
            />
          </div>

          {/* Sessions section */}
          <div>
            <div className="flex items-center justify-between">
              <h3
                className="text-sm font-semibold text-foreground"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                Terapijske sesije
              </h3>
              <span className="text-xs text-muted-foreground">
                {sessionTotal} ukupno
              </span>
            </div>

            {/* Session filter pills */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {[
                { value: "all", label: "Sve" },
                { value: "scheduled", label: "Zakazane" },
                { value: "completed", label: "Završene" },
                { value: "cancelled", label: "Otkazane" },
                { value: "no_show", label: "Nije došao" },
              ].map((f) => (
                <button
                  key={f.value}
                  onClick={() => {
                    setSessionFilter(f.value);
                    setSessionPage(1);
                  }}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-medium transition-all",
                    sessionFilter === f.value
                      ? "bg-foreground text-background"
                      : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Sessions list */}
            <div className="mt-3 space-y-2">
              {sessionsLoading && sessions.length === 0 ? (
                <>
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 rounded-lg border bg-muted/20 px-3 py-2.5"
                    >
                      <div className="size-8 animate-pulse rounded-lg bg-muted" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
                        <div className="h-3 w-1/2 animate-pulse rounded bg-muted/60" />
                      </div>
                    </div>
                  ))}
                </>
              ) : sessions.length === 0 ? (
                <p className="rounded-xl border border-dashed border-border/60 bg-muted/20 py-8 text-center text-sm text-muted-foreground">
                  {sessionFilter === "all"
                    ? "Ovaj klijent nema terapijskih sesija."
                    : "Nema sesija s ovim statusom."}
                </p>
              ) : (
                sessions.map((session) => (
                  <SessionRow key={session.id} session={session} />
                ))
              )}
            </div>

            {/* Sessions pagination */}
            {sessionTotalPages > 1 && (
              <div className="mt-4">
                <SessionsPagination
                  page={sessionPage}
                  totalPages={sessionTotalPages}
                  total={sessionTotal}
                  pageSize={SESSIONS_PER_PAGE}
                  onPageChange={setSessionPage}
                />
              </div>
            )}
          </div>
        </div>
      </DialogBody>

      <DialogFooter>
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <ClientBlockControls client={client} onDone={onClose} />
          <Button variant="outline" onClick={onClose}>
            Zatvori
          </Button>
        </div>
      </DialogFooter>
    </Dialog>
  );
}
